import { useState, useEffect } from 'react';
import api from '../api';
import { downloadExcel } from '../utils/excelExport';

const TABS = [
  { key: 'directions', label: 'Направления' },
  { key: 'articles', label: 'Статьи' },
  { key: 'sources', label: 'Источники' },
  { key: 'projects', label: 'Проекты' },
];

const OP_TYPE_LABELS = { expense: 'Расход', income: 'Приход' };
const OPERATION_TYPE_LABELS = { приход: 'Приход', поступление: 'Поступление', расход: 'Расход', выбытие: 'Выбытие' };
const ACTIVITY_LABELS = { operating: 'Операционная', investing: 'Инвестиционная', financing: 'Финансовая' };
const COST_LABELS = { variable: 'Переменные', fixed: 'Постоянные', commercial: 'Коммерческие', administrative: 'Административные', tax: 'Налоги', depreciation: 'Амортизация', interest: 'Проценты по кредитам' };

export default function References() {
  const [tab, setTab] = useState('directions');
  const [items, setItems] = useState([]);
  const [directions, setDirections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [newDirectionId, setNewDirectionId] = useState('');
  const [newOpType, setNewOpType] = useState('expense');
  const [newActivityType, setNewActivityType] = useState('operating');
  const [newCostType, setNewCostType] = useState('');
  const [error, setError] = useState('');
  const [selected, setSelected] = useState([]);
  const [showBulk, setShowBulk] = useState(false);
  const [bulkDirectionId, setBulkDirectionId] = useState('');
  const [bulkOperationType, setBulkOperationType] = useState('');
  const [bulkActivityType, setBulkActivityType] = useState('');
  const [bulkCostType, setBulkCostType] = useState('');
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkError, setBulkError] = useState('');

  const load = () => {
    setLoading(true);
    api.get(`/references/${tab}`).then(r => setItems(r.data)).finally(() => setLoading(false));
    if (tab === 'articles') api.get('/references/directions').then(r => setDirections(r.data));
  };

  useEffect(() => { load(); setNewName(''); setNewDirectionId(''); setNewCostType(''); setError(''); setSelected([]); setShowBulk(false); }, [tab]);

  const add = async (e) => {
    e.preventDefault();
    setError('');
    if (!newName.trim()) return;
    try {
      const payload = { name: newName.trim() };
      if (tab === 'articles') {
        payload.direction_id = newDirectionId || null;
        payload.activity_type = newActivityType;
        payload.cost_type = newCostType || null;
      }
      if (tab === 'directions') payload.op_type = newOpType;
      await api.post(`/references/${tab}`, payload);
      setNewName(''); setNewDirectionId(''); setNewCostType('');
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка добавления');
    }
  };

  const toggleActive = async (item) => {
    await api.patch(`/references/${tab}/${item.id}`, { is_active: !item.is_active });
    load();
  };

  const updateField = async (item, field, value) => {
    await api.patch(`/references/${tab}/${item.id}`, { [field]: value });
    load();
  };

  const remove = async (item) => {
    if (!window.confirm(`Удалить «${item.name}»?`)) return;
    await api.delete(`/references/${tab}/${item.id}`);
    load();
  };

  const toggleSelected = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const allSelected = items.length > 0 && selected.length === items.length;
  const toggleSelectAll = () => setSelected(allSelected ? [] : items.map(i => i.id));

  const applyBulk = async () => {
    setBulkError('');
    const payload = { ids: selected };
    if (bulkDirectionId !== '') payload.direction_id = Number(bulkDirectionId);
    if (bulkOperationType !== '') payload.operation_type = bulkOperationType;
    if (bulkActivityType !== '') payload.activity_type = bulkActivityType;
    if (bulkCostType !== '') payload.cost_type = bulkCostType;
    if (payload.direction_id === undefined && payload.operation_type === undefined
      && payload.activity_type === undefined && payload.cost_type === undefined) {
      setBulkError('Укажите, что изменить');
      return;
    }
    setBulkSaving(true);
    try {
      await api.patch('/references/articles/bulk', payload);
      setShowBulk(false); setSelected([]);
      setBulkDirectionId(''); setBulkOperationType(''); setBulkActivityType(''); setBulkCostType('');
      load();
    } catch (err) {
      setBulkError(err.response?.data?.error || 'Ошибка сохранения');
    } finally { setBulkSaving(false); }
  };

  const exportItems = () => {
    const headers = ['Название'];
    if (tab === 'articles') headers.push('Раздел бюджета', 'Операция', 'Вид деятельности', 'Тип издержек');
    if (tab === 'directions') headers.push('Тип операции');
    headers.push('Статус');
    const rows = items.map(item => {
      const row = [item.name];
      if (tab === 'articles') {
        row.push(
          directions.find(d => d.id === item.direction_id)?.name || '—',
          OPERATION_TYPE_LABELS[item.operation_type] || '—',
          ACTIVITY_LABELS[item.activity_type] || '—',
          COST_LABELS[item.cost_type] || '—',
        );
      }
      if (tab === 'directions') row.push(OP_TYPE_LABELS[item.op_type] || '—');
      row.push(item.is_active ? 'Активна' : 'Отключена');
      return row;
    });
    downloadExcel(TABS.find(t => t.key === tab)?.label || 'Справочник', headers, rows);
  };

  return (
    <div>
      <div className="toolbar">
        {TABS.map(t => (
          <button key={t.key} className={tab === t.key ? 'btn-primary btn-sm' : 'btn-secondary btn-sm'} onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
        <div className="toolbar-spacer" />
        {items.length > 0 && <button className="btn-secondary btn-sm" onClick={exportItems}>Экспорт в Excel</button>}
      </div>

      <form className="toolbar" onSubmit={add}>
        <input placeholder="Название" value={newName} onChange={e => setNewName(e.target.value)} style={{ minWidth: 220 }} />
        {tab === 'articles' && (
          <>
            <select value={newDirectionId} onChange={e => setNewDirectionId(e.target.value)}>
              <option value="">Без раздела</option>
              {directions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
            <select value={newActivityType} onChange={e => setNewActivityType(e.target.value)}>
              {Object.entries(ACTIVITY_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <select value={newCostType} onChange={e => setNewCostType(e.target.value)}>
              <option value="">Тип издержек: —</option>
              {Object.entries(COST_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </>
        )}
        {tab === 'directions' && (
          <select value={newOpType} onChange={e => setNewOpType(e.target.value)}>
            {Object.entries(OP_TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        )}
        <button type="submit" className="btn-primary btn-sm">Добавить</button>
        {tab === 'articles' && selected.length > 0 && (
          <button type="button" className="btn-secondary btn-sm" onClick={() => setShowBulk(true)}>Изменить выбранные ({selected.length})</button>
        )}
      </form>
      {error && <div className="error-msg">{error}</div>}

      {showBulk && (
        <div className="toolbar" style={{ background: '#f5f5f5', padding: 8, borderRadius: 6, marginBottom: 8 }}>
          <span>Выбрано статей: {selected.length}</span>
          <select value={bulkDirectionId} onChange={e => setBulkDirectionId(e.target.value)}>
            <option value="">Раздел — не менять</option>
            {directions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
          <select value={bulkOperationType} onChange={e => setBulkOperationType(e.target.value)}>
            <option value="">Операция — не менять</option>
            {Object.entries(OPERATION_TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select value={bulkActivityType} onChange={e => setBulkActivityType(e.target.value)}>
            <option value="">Вид деятельности — не менять</option>
            {Object.entries(ACTIVITY_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select value={bulkCostType} onChange={e => setBulkCostType(e.target.value)}>
            <option value="">Тип издержек — не менять</option>
            {Object.entries(COST_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          {bulkError && <span className="error-msg">{bulkError}</span>}
          <button className="btn-primary btn-sm" disabled={bulkSaving} onClick={applyBulk}>{bulkSaving ? 'Сохранение...' : 'Применить'}</button>
          <button className="btn-secondary btn-sm" onClick={() => { setShowBulk(false); }}>Отмена</button>
        </div>
      )}

      {loading ? <div className="loading-text">Загрузка...</div> : items.length === 0 ? (
        <div className="empty-state">Список пуст</div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                {tab === 'articles' && <th><input type="checkbox" checked={allSelected} onChange={toggleSelectAll} /></th>}
                <th>Название</th>
                {tab === 'articles' && <th>Раздел бюджета</th>}
                {tab === 'articles' && <th>Операция</th>}
                {tab === 'articles' && <th>Вид деятельности</th>}
                {tab === 'articles' && <th>Тип издержек</th>}
                {tab === 'directions' && <th>Тип операции</th>}
                <th>Статус</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map(item => (
                <tr key={item.id}>
                  {tab === 'articles' && (
                    <td><input type="checkbox" checked={selected.includes(item.id)} onChange={() => toggleSelected(item.id)} /></td>
                  )}
                  <td>{item.name}</td>
                  {tab === 'articles' && <td>{directions.find(d => d.id === item.direction_id)?.name || '—'}</td>}
                  {tab === 'articles' && (
                    <td>
                      <select value={item.operation_type || ''} onChange={e => updateField(item, 'operation_type', e.target.value || null)}>
                        <option value="">—</option>
                        {Object.entries(OPERATION_TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    </td>
                  )}
                  {tab === 'articles' && (
                    <td>
                      <select value={item.activity_type || 'operating'} onChange={e => updateField(item, 'activity_type', e.target.value)}>
                        {Object.entries(ACTIVITY_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    </td>
                  )}
                  {tab === 'articles' && (
                    <td>
                      <select value={item.cost_type || ''} onChange={e => updateField(item, 'cost_type', e.target.value || null)}>
                        <option value="">—</option>
                        {Object.entries(COST_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    </td>
                  )}
                  {tab === 'directions' && (
                    <td>
                      <select value={item.op_type || 'expense'} onChange={e => updateField(item, 'op_type', e.target.value)}>
                        {Object.entries(OP_TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    </td>
                  )}
                  <td>
                    <span className={`badge ${item.is_active ? 'badge-paid' : 'badge-rejected'}`}>
                      {item.is_active ? 'Активна' : 'Отключена'}
                    </span>
                  </td>
                  <td>
                    <button className="btn-secondary btn-sm" onClick={() => toggleActive(item)}>
                      {item.is_active ? 'Отключить' : 'Включить'}
                    </button>{' '}
                    <button className="btn-danger btn-sm" onClick={() => remove(item)}>Удалить</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
